import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { styled } from '@mui/material/styles';
import {
    MouseEventHandler,
    ReactNode,
    createContext,
    useContext,
    useEffect,
    useId,
    useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import { ScreenContext } from '@/contexts/ScreenContext';
import { useRwdUI } from '@/hooks/useRwdUI';
import { RwdSvgIcon } from '@/modules/rwdGame/components/RwdSvgIcon/RwdSvgIcon';
import { EmptyIcon } from '@/modules/setting/icon';
import DoneIcon from '@mui/icons-material/Done';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import { PopoverOrigin } from '@mui/material/Popover';
import { SvgIconProps } from '@mui/material/SvgIcon';
import { useDropdown, useDropdownState } from './hook';
import './index.scss';

type DropdownProps = {
    placeholder: string | ReactNode;
    children: ReactNode;
    className: string;
    disableTranslation?: boolean;
    disableAutoWidth?: boolean;
    icon?: ReactNode;
    transformOrigin?: PopoverOrigin;
    anchorOrigin?: PopoverOrigin;
    mt?: number;
    endIconProps?: SvgIconProps;
};

const DropdownButton = styled(Button)({
    variants: 'contained',
});

const DropdownMenu = styled(Menu)({});

const DropdownContext = createContext({} as useDropdownState);
const { Provider: DropdownProvider } = DropdownContext;

export const Dropdown = (props: DropdownProps) => {
    const id = `dropdown-${useId()}`;
    const {
        children,
        placeholder,
        className,
        disableTranslation = false,
        disableAutoWidth = false,
        icon = <></>,
        endIconProps,
        transformOrigin = { horizontal: 'left', vertical: 'top' },
        anchorOrigin = { horizontal: 'left', vertical: 'bottom' },
        mt = 1,
        ...otherProps
    } = props;
    const { t } = useTranslation();
    const controller = useDropdown();
    const { anchorEl, open, handleOpen, handleClose } = controller;
    const [width, setWidth] = useState<number | string>('auto');
    const [height, setHeight] = useState<number | string>('auto');

    useEffect(() => {
        if (!disableAutoWidth) {
            if (anchorEl !== null) {
                const w = anchorEl.offsetWidth;
                // const t = anchorEl.offsetTop;
                setWidth(w);
                // setHeight(`calc(var(--vh) * 100 - ${t}px - 6rem)`);
                setHeight('auto');
            } else {
                setWidth('auto');
            }
        }
    }, [anchorEl, disableAutoWidth]);
    return (
        <DropdownProvider value={controller}>
            <Box className={className}>
                <DropdownButton
                    aria-controls={open ? id : undefined}
                    aria-haspopup={true}
                    aria-expanded={open ? true : undefined}
                    onClick={handleOpen}
                    startIcon={icon ?? <></>}
                    endIcon={
                        open ? (
                            <ArrowDropUpIcon {...endIconProps} />
                        ) : (
                            <ArrowDropDownIcon {...endIconProps} />
                        )
                    }
                    disableElevation
                    fullWidth
                    className={`dropdown-button ${open ? 'selected' : 'unselected'}`}
                >
                    <span className="dropdown-label">
                        {typeof placeholder === 'string'
                            ? disableTranslation
                                ? placeholder
                                : t(placeholder)
                            : placeholder}
                    </span>
                </DropdownButton>
                <DropdownMenu
                    id={id}
                    className="dropdown-menu"
                    anchorEl={anchorEl}
                    open={open}
                    onClose={handleClose}
                    MenuListProps={{
                        'aria-labelledby': id,
                        sx: {
                            width: `${width}px !important`,
                            maxHeight: height,
                        },
                    }}
                    slotProps={{
                        paper: {
                            sx: {
                                mt: mt,
                            },
                        },
                    }}
                    transformOrigin={transformOrigin}
                    anchorOrigin={anchorOrigin}
                    {...otherProps}
                >
                    {children}
                </DropdownMenu>
            </Box>
        </DropdownProvider>
    );
};
type DropdownItemProps = {
    onClick?: MouseEventHandler<HTMLLIElement> | undefined;
    isChecked?: boolean;
    text: ReactNode;
};

export const DropdownItem = (props: DropdownItemProps) => {
    const { onClick, isChecked = false, text } = props;
    const { handleClose } = useContext(DropdownContext);
    const { pxToRwdString } = useRwdUI();
    const { isLandscape, isMobile } = useContext(ScreenContext);

    const [fontSizeValue, setFontSizeValue] = useState<string>('18px');

    useEffect(() => {
        setFontSizeValue(isLandscape && !isMobile ? pxToRwdString(32) : '18px');
    }, [isLandscape]);

    return (
        <MenuItem
            onClick={event => {
                if (onClick) onClick(event);
                handleClose && handleClose();
            }}
            className={isChecked ? 'checked' : ''}
            sx={{ gap: pxToRwdString(8), padding: 0 }}
        >
            <ListItemIcon>
                {isChecked ? (
                    <RwdSvgIcon
                        className="item-icon"
                        portraitIconSize={20}
                        landscapeIconSize={32}
                    >
                        <DoneIcon />
                    </RwdSvgIcon>
                ) : (
                    <RwdSvgIcon
                        className="item-icon"
                        portraitIconSize={20}
                        landscapeIconSize={32}
                    >
                        <EmptyIcon />
                    </RwdSvgIcon>
                )}
            </ListItemIcon>
            <ListItemText
                primaryTypographyProps={{
                    fontSize: fontSizeValue,
                }}
                primary={text}
            />
        </MenuItem>
    );
};
